// src/e2ee/tofu.ts

import type { KnownAgentKey, KeyExchangeResult } from './types';

const STORAGE_KEY = 'whss-known-keys';

/**
 * Get all known keys from localStorage
 */
function getKnownKeys(): Map<string, KnownAgentKey> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Map();
    const arr: KnownAgentKey[] = JSON.parse(stored);
    return new Map(arr.map(k => [k.subdomain, k]));
  } catch {
    return new Map();
  }
}

/**
 * Save known keys to localStorage
 */
function saveKnownKeys(keys: Map<string, KnownAgentKey>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys.values()]));
}

/**
 * Get fingerprint (first 8 chars) of public key
 */
export function getKeyFingerprint(publicKey: string): string {
  return publicKey.substring(0, 8);
}

/**
 * Get known key for subdomain
 */
export function getKnownKey(subdomain: string): KnownAgentKey | undefined {
  return getKnownKeys().get(subdomain);
}

/**
 * Verify agent's public key against stored key (TOFU)
 */
export function verifyAgentKey(
  subdomain: string,
  publicKey: string
): KeyExchangeResult {
  const known = getKnownKey(subdomain);

  if (!known) {
    // First time seeing this agent - trust and store
    storeKey(subdomain, publicKey);
    return { success: true, keyChanged: false };
  }

  if (known.publicKey === publicKey) {
    // Key matches - update last seen
    const keys = getKnownKeys();
    known.lastSeen = Date.now();
    keys.set(subdomain, known);
    saveKnownKeys(keys);
    return { success: true, keyChanged: false };
  }

  // Key changed! Return warning
  return {
    success: false,
    keyChanged: true,
    previousFingerprint: getKeyFingerprint(known.publicKey),
    newFingerprint: getKeyFingerprint(publicKey),
  };
}

/**
 * Store new key for subdomain
 */
export function storeKey(subdomain: string, publicKey: string): void {
  const keys = getKnownKeys();
  keys.set(subdomain, {
    subdomain,
    publicKey,
    fingerprint: getKeyFingerprint(publicKey),
    firstSeen: Date.now(),
    lastSeen: Date.now(),
  });
  saveKnownKeys(keys);
}

/**
 * Accept new key (user confirmed key change)
 */
export function acceptNewKey(subdomain: string, publicKey: string): void {
  storeKey(subdomain, publicKey);
}
